import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import { saquesApi, ListSaquesParams } from '../../services/api/saques.api';
import {
  PaginatedSaques,
  SaqueDetail,
  SaqueListItem,
  SaquesFilters,
} from '../../types/saque';

export interface SaquesState {
  items: SaqueListItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  filters: SaquesFilters;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  selectedSaqueId: string | null;
  selectedDetail: SaqueDetail | null;
  detailStatus: 'idle' | 'loading' | 'error';
  detailError: string | null;
  mutating: { id: string; action: 'confirm' | 'refuse' } | null;
  mutationError: string | null;
}

const initialState: SaquesState = {
  items: [],
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  filters: {},
  status: 'idle',
  error: null,
  selectedSaqueId: null,
  selectedDetail: null,
  detailStatus: 'idle',
  detailError: null,
  mutating: null,
  mutationError: null,
};

const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.code === 'ERR_NETWORK') {
      return 'Não foi possível conectar ao servidor.';
    }
  }
  return fallback;
};

export const fetchSaquesThunk = createAsyncThunk<
  PaginatedSaques,
  ListSaquesParams,
  { rejectValue: string }
>('saques/fetch', async (params, { rejectWithValue }) => {
  try {
    return await saquesApi.list(params);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar saques'));
  }
});

export const fetchSaqueDetailThunk = createAsyncThunk<
  SaqueDetail,
  string,
  { rejectValue: string }
>('saques/fetchDetail', async (id, { rejectWithValue }) => {
  try {
    return await saquesApi.detail(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar detalhe'));
  }
});

export const confirmSaqueThunk = createAsyncThunk<
  SaqueDetail,
  string,
  { rejectValue: string }
>('saques/confirm', async (id, { rejectWithValue }) => {
  try {
    return await saquesApi.confirm(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Não foi possível confirmar'));
  }
});

export const refuseSaqueThunk = createAsyncThunk<
  SaqueDetail,
  string,
  { rejectValue: string }
>('saques/refuse', async (id, { rejectWithValue }) => {
  try {
    return await saquesApi.refuse(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Não foi possível recusar'));
  }
});

const saquesSlice = createSlice({
  name: 'saques',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<SaquesFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearFilters(state) {
      state.filters = {};
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
      state.page = 1;
    },
    selectSaque(state, action: PayloadAction<string | null>) {
      state.selectedSaqueId = action.payload;
      if (action.payload === null) {
        state.selectedDetail = null;
        state.detailStatus = 'idle';
        state.detailError = null;
        state.mutationError = null;
      }
    },
    clearMutationError(state) {
      state.mutationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSaquesThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSaquesThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload.data;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchSaquesThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar saques';
      })

      .addCase(fetchSaqueDetailThunk.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchSaqueDetailThunk.fulfilled, (state, action) => {
        state.detailStatus = 'idle';
        state.selectedDetail = action.payload;
      })
      .addCase(fetchSaqueDetailThunk.rejected, (state, action) => {
        state.detailStatus = 'error';
        state.detailError = action.payload ?? 'Erro ao carregar detalhe';
      })

      .addCase(confirmSaqueThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg, action: 'confirm' };
        state.mutationError = null;
      })
      .addCase(confirmSaqueThunk.fulfilled, (state, action) => {
        state.mutating = null;
        state.selectedDetail = action.payload;
      })
      .addCase(confirmSaqueThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Não foi possível confirmar';
      })

      .addCase(refuseSaqueThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg, action: 'refuse' };
        state.mutationError = null;
      })
      .addCase(refuseSaqueThunk.fulfilled, (state, action) => {
        state.mutating = null;
        state.selectedDetail = action.payload;
      })
      .addCase(refuseSaqueThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Não foi possível recusar';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setLimit,
  selectSaque,
  clearMutationError,
} = saquesSlice.actions;

export default saquesSlice.reducer;
